import type { SubmitHandler } from "react-hook-form"

import { useEquivalencyGroup } from "./useEquivalencyGroup"

import type {
  EquivalencyGroup,
  EquivalencyGroupCreateInput,
  EquivalencyGroupCreateSuccess,
  EquivalencyGroupDeleteInput,
  EquivalencyGroupUpdateInput,
  EquivalencyGroupUpdateSuccess,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseEquivalentProductGroupActionsReturn {
  createEquivalentProductGroup: SubmitHandler<EquivalencyGroupCreateInput>
  updateEquivalentProductGroup: SubmitHandler<EquivalencyGroupUpdateInput>
  deleteEquivalentProductGroup: (input: EquivalencyGroupDeleteInput) => Promise<boolean>
}

interface UseEquivalentProductGroupActionsProps {
  onSuccess?: (data?: EquivalencyGroup) => Promise<void> | void
}

export const useEquivalentProductGroupActions = ({
  onSuccess,
}: UseEquivalentProductGroupActionsProps = {}): UseEquivalentProductGroupActionsReturn => {
  const { createEquivalencyGroup, updateEquivalencyGroup, deleteEquivalencyGroup } = useEquivalencyGroup()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const createEquivalentProductGroup: SubmitHandler<EquivalencyGroupCreateInput> = async (input) => {
    const { error, data } = await createEquivalencyGroup({ input })

    const hasError =
      error?.message ||
      (data?.equivalencyGroup.create.__typename === "EquivalencyGroupFailure" &&
        Boolean(data?.equivalencyGroup.create.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to create equivalents group",
      })
      return
    }

    const equivalencyGroup = (data?.equivalencyGroup.create as EquivalencyGroupCreateSuccess).equivalencyGroup
    await onSuccess?.(equivalencyGroup)

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "New Equivalents Group successfully created",
    })
  }

  const updateEquivalentProductGroup: SubmitHandler<EquivalencyGroupUpdateInput> = async (input) => {
    const { error, data } = await updateEquivalencyGroup({ input })

    const hasError =
      error?.message ||
      (data?.equivalencyGroup.update.__typename === "EquivalencyGroupFailure" &&
        Boolean(data?.equivalencyGroup.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update equivalents group",
      })
      return
    }

    const equivalencyGroup = (data?.equivalencyGroup.update as EquivalencyGroupUpdateSuccess).equivalencyGroup
    await onSuccess?.(equivalencyGroup)

    notify({
      type: NotificationType.Success,
      silent: false,
      content: `${equivalencyGroup.name} changes saved.`,
    })
  }

  const deleteEquivalentProductGroup = async (input: EquivalencyGroupDeleteInput): Promise<boolean> => {
    const { error, data } = await deleteEquivalencyGroup({ input })

    const hasError =
      error?.message ||
      (data?.equivalencyGroup.delete.__typename === "EquivalencyGroupFailure" &&
        Boolean(data?.equivalencyGroup.delete.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to delete equivalency group",
      })
      return false
    }

    await onSuccess?.()

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Equivalency group deleted successfully",
    })

    return true
  }

  return {
    createEquivalentProductGroup,
    updateEquivalentProductGroup,
    deleteEquivalentProductGroup,
  }
}

export default useEquivalentProductGroupActions
